<script lang="ts" setup>
    import { onMounted, ref, reactive, computed } from 'vue';

    import DatePickerNoTZ from '../../components/DatePickerNoTZ.vue';
    import ChoiceTable from "../../components/ChoiceTable.vue";
    import MessageSection from '../../components/Messages/MessageSection.vue'
    import Icon from '../../components/Icon.vue'
    import Tab from "../../components/Tab.vue"
    import ActionButtonV2 from '../../components/ActionButtonV2.vue'
    import ModalDialog from '../../components/ModalDialog.vue'
    import ResizeMonitor from '../../components/ResizeMonitor.vue'

    import { ICol, ValueStrDelegate } from "../../lib/ChoiceTableHelpers";
    import { ccdaBuilder } from '../../lib/ChoiceApi';
    import { CCDABuildStatus } from "../../Enums/ChoiceEnums";
    import { TableRowType, MessageSectionType, DisplayStyle, IconTypes, LayoutOrientation, ButtonDisplayStyle, ButtonDisplaySize, ModalSize } from "../../Enums/UiEnums";
    import { ICCDACaseSummaryItem, ICCDACaseSummaryResponseItem } from '../../lib/Models/CCDAModel';
    import { CCDViewer, CaseReportResponseViewer, CaseReportAttachmentViewer } from '../../lib/OfficeMdUrls';
    import { ActionButtonStyle } from "../../lib/Types/Types";
    import { popAlert } from '../../lib/Notification';
    import '../../../css/choice.css';

    const now = new Date();
    
    enum CaseReportSummaryTabName {
        Sent = 0,
        Unsolicited = 1
    };

    const tableHeight = ref<number>();

    const formData = reactive <
    {    
        startDate: string,
        stopDate: string,
        searching: boolean,
        displayErrorMessage: string,
        tableData: ICCDACaseSummaryItem[],
        caseReportResponseList: ICCDACaseSummaryResponseItem[],
        unsolicitedList: ICCDACaseSummaryResponseItem[],
        tabName: CaseReportSummaryTabName,
        showExportQuestion: boolean
    }>({ 
        startDate: now.toLocaleDateString(),
        stopDate: now.toLocaleDateString(),
        searching: false,
        displayErrorMessage: "",
        tableData: [],
        caseReportResponseList: [],
        unsolicitedList: [],    
        tabName: CaseReportSummaryTabName.Sent,
        showExportQuestion: false
    });

    const FormatName: ValueStrDelegate = (value: any, col: ICol, row: Record<string, any> | undefined, rowType: TableRowType): string | undefined => {
        if (rowType === TableRowType.Body)
        {
             return row?.patientName + 
                (row?.birthDate.length > 0 ? " " + row?.birthDate + " (" + row?.age + ")" : "") + 
                " " + row?.gender;
        }
    }

    const UnsolicitedFormatName: ValueStrDelegate = (value: any, col: ICol, row: Record<string, any> | undefined, rowType: TableRowType): string | undefined => {
        if (rowType === TableRowType.Body)
        {
             return `${row?.patientLastName}, ${row?.patientFirstName} ${row?.birthTime} ${row?.gender}`;
        }
    }

    const FormatStatus: ValueStrDelegate = (value: any, col: ICol, row: Record<string, any> | undefined, rowType: TableRowType): string | undefined => {
        if (rowType === TableRowType.Body)
        {
            if (HasCaseReportResponse(row?.caseReportId))
                return "Completed";
            switch (row?.status) {
                case CCDABuildStatus.Failed:
                    return "Failed";
                case CCDABuildStatus.Sent:
                    return "Sent / Waiting for Confirmation";
                case CCDABuildStatus.Processing:
                    return "Processing";
                case CCDABuildStatus.Built:
                    return "Built / Waiting to Send";
                case CCDABuildStatus.Completed:
                    return "Waiting for Response";
                default:
                    return "Queued";
            }
        }
    }

    const tableColumns = ref<ICol[]>([
        { Name: 'Identifier', DataField: 'recordId' },
        { Name: 'Chart #', DataField: 'patientId' },
        { Name: 'Patient', DataField: 'patientName', FormatValue: FormatName },
        { Name: 'Encounter', DataField: 'encounterDate' },
        { Name: 'Status', DataField: 'status', FormatValue: FormatStatus },
        { Name: 'Processing', DataField: '' },
        { Name: 'Preview', DataField: '' },
    ]);

    const unsolicitedColumns = ref<ICol[]>([
        { Name: 'Identifier', DataField: 'caseReportResponseId' },
        { Name: 'Attached Chart #', DataField: 'attachPatientId' },
        { Name: 'Patient', DataField: 'patientName', FormatValue: UnsolicitedFormatName },
        { Name: 'Preview', DataField: '' },
    ]);

    onMounted(async () => {
        windowResize();
        CaseReportSummary();
    });

    function windowResize() {
        tableHeight.value = window.innerHeight - 110;
    }

    const exportDisabled = computed<boolean>(() => formData.tableData.length == 0);
    const unsolicitedResults = computed<boolean>(() => formData.unsolicitedList.length != 0);

    async function CaseReportSummary() {
        formData.displayErrorMessage = "";
        if (formData.startDate.length == 10 && formData.stopDate.length == 10) {
            if (Date.parse(formData.startDate) > Date.parse(formData.stopDate)) {
                formData.displayErrorMessage = "Error: Start Date cannot be greater than the Stop Date";
                setTimeout(() => formData.searching = false, 250);
                return;
            }
        }
        const post_data = {
            startDate: formData.startDate,
            stopDate: formData.stopDate
        }
        formData.searching = true;
        const resp = await ccdaBuilder.CaseReportSummary(post_data);
        formData.searching = false;
        formData.tableData = resp.caseReportList;
        formData.caseReportResponseList = resp.caseReportResponseList;
        formData.unsolicitedList = formData.caseReportResponseList.filter(item => item.caseReportId == 0);
    }

    async function CaseReportExport(exportNow: boolean = false, includeUnsolicited: boolean = false) {
        if (!exportNow) {
            formData.showExportQuestion = true;
            return;
        }
        formData.showExportQuestion = false;

        const post_data = {
            startDate: formData.startDate,
            stopDate: formData.stopDate,
            includeUnsolicited: includeUnsolicited
        }
        const resp = await ccdaBuilder.CaseReportExport(post_data);
        if (resp == null) {
            await popAlert("Failed to retrieve the case report");
            return; 
        }
        try {
            const wb = XLSX.utils.book_new()
            const ws1 = XLSX.utils.json_to_sheet(resp);
            XLSX.utils.book_append_sheet(wb, ws1, "CaseReportSummary");
            XLSX.writeFile(wb, "CaseReportSummary.xls");
        }
        catch (e) {
            await popAlert("Error downloading data to Excel: " + e);
        }	
    }

    function HasCaseReportResponse(caseReportId: number) {
        return formData.caseReportResponseList.some((item) => item.caseReportId === caseReportId);
    }

    function PreviewSent(row: ICCDACaseSummaryItem) {
        CCDViewer(row.recordId, true);
    }

    function PreviewReceived(row: ICCDACaseSummaryItem) {
        CaseReportResponseViewer(row.caseReportId, true);
    }

    function PreviewReceivedUnSolicited(row: ICCDACaseSummaryResponseItem) {
        CaseReportAttachmentViewer(row?.directAttachmentId ?? 0, row.filename, true);
    }

    async function onHideExportQuestion() {
        formData.showExportQuestion = false;
    }   

</script>

<style>
    .caseReportSummaryLabelWidth {
        width: 40px;
    }
    .caseReportSummaryDateWidth {
        width: 180px;
    }
</style>

<template>
    <ResizeMonitor @resize="windowResize" />
    <div class="row mt-2">
        <div class="caseReportSummaryLabelWidth label-isalus-small mt-2">
            Start
        </div>
        <div class="caseReportSummaryDateWidth">
            <DatePickerNoTZ v-model:value="formData.startDate" :cssFramework="'vanilla'" :clearable="false" />
        </div>
        <div class="caseReportSummaryLabelWidth label-isalus-small mt-2">
            Stop
        </div>
        <div class="caseReportSummaryDateWidth">
            <DatePickerNoTZ v-model:value="formData.stopDate" :cssFramework="'vanilla'" :clearable="false" />
        </div>
        <div class="col">
            <ActionButtonV2 
                :action="ActionButtonStyle.Search" 
                text="Search"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :performAction="CaseReportSummary"
                :size="ButtonDisplaySize.Small"
                :endingPadding="1" />
            <ActionButtonV2 
                :action="ActionButtonStyle.Export" 
                text="Export"
                :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                :disabled="exportDisabled"
                :performAction="() => CaseReportExport(!unsolicitedResults, unsolicitedResults)"
                :size="ButtonDisplaySize.Small"
                :endingPadding="1" />
        </div>
        <div class="col mt-2">
            <MessageSection v-if="formData.displayErrorMessage" :show-icon="false" :type="MessageSectionType.Text" :display-style="DisplayStyle.Danger">
                {{ formData.displayErrorMessage }}
            </MessageSection>    
        </div>
    </div>
    <div class="row mt-2">
        <div class="col">
            <Tab :value="CaseReportSummaryTabName.Sent" v-model="formData.tabName" :layout="LayoutOrientation.Horz">                    
                Sent
            </Tab>
            <Tab :value="CaseReportSummaryTabName.Unsolicited" v-model="formData.tabName" :layout="LayoutOrientation.Horz">                    
                Unsolicited
            </Tab>
        </div>
    </div>
    <div class="row mt-2" v-if="formData.tabName == CaseReportSummaryTabName.Sent">
        <div class="col">
            <ChoiceTable 
                :columns="tableColumns" 
                :data="formData.tableData" 
                :show-filters="false" 
                :allowSorting="true"
                :height="tableHeight"
                fontSize="smaller"
                :key-value-data-member="'Col3Val'">
                <template #cell="{col, row, value}">
                    <template v-if="'Processing' === col.Name">
                        <template v-if="row.buildDate != ''">
                            <Icon :icon="IconTypes.Complete" :title="`Built: ${row.buildDate}`" :displayStyle="DisplayStyle.Success" :endingPadding="2"/>

                            <template v-if="row.sentDate != ''">
                                <Icon :icon="IconTypes.Complete" :title="`Sent: ${row.sentDate}`" :displayStyle="DisplayStyle.Success" :endingPadding="2"/>

                                <template v-if="row.statusDate != ''">
                                    <Icon :icon="IconTypes.Complete" :title="`Send Confirmation: ${row.statusDate}`" :displayStyle="DisplayStyle.Success" :endingPadding="2"/>

                                    <template v-if="HasCaseReportResponse(row.caseReportId)">
                                        <Icon :icon="IconTypes.Complete" title="Case Report Received" :displayStyle="DisplayStyle.Success"/>
                                    </template>
                                    <template v-else>
                                        <Icon :icon="IconTypes.Waiting" title="Waiting for Case Report Response" :displayStyle="DisplayStyle.Primary"/>
                                    </template>
                                </template>
                                <template v-else>
                                    <template v-if="row.status != CCDABuildStatus.Failed">
                                        <Icon :icon="IconTypes.Waiting" title="Waiting for Send Confirmation" :displayStyle="DisplayStyle.Primary"/>
                                    </template>
                                    <template v-else>
                                        <Icon :icon="IconTypes.CircleXMark" title="Send Confirmation Failed" :displayStyle="DisplayStyle.Danger"/>
                                    </template>
                                </template>
                            </template>
                            <template v-else>
                                <template v-if="row.status != CCDABuildStatus.Failed">
                                    <Icon :icon="IconTypes.Waiting" title="Waiting to Send" :displayStyle="DisplayStyle.Primary"/>
                                </template>
                                <template v-else>
                                    <Icon :icon="IconTypes.CircleXMark" title="Send Failed" :displayStyle="DisplayStyle.Danger"/>
                                </template>
                            </template>
                        </template>
                        <template v-else>
                            <template v-if="row.status != CCDABuildStatus.Failed">
                                <Icon :icon="IconTypes.Waiting" :title="`Queued ${row.sendDate}`" :displayStyle="DisplayStyle.Primary"/>
                            </template>
                            <template v-else>
                                <Icon :icon="IconTypes.CircleXMark" title="Build Failed" :displayStyle="DisplayStyle.Danger"/>
                            </template>
                        </template>
                        <template v-if="row.priority">
                            <Icon :icon="IconTypes.Star" title="Priority Request" :displayStyle="DisplayStyle.Primary" :startingPadding="2"/>
                        </template>
                    </template>
                    <template v-if="'Preview' === col.Name">
                        <template v-if="row.buildDate != ''">
                            <button class="btn btn-primary btn-sm ms-1 pt-0 pb-0" title="Preview what was Sent" @click="PreviewSent(row)">
                                Sent
                            </button>
                        </template>
                        <template v-if="HasCaseReportResponse(row.caseReportId)">
                            <button class="btn btn-primary btn-sm ms-1 pt-0 pb-0" title="Preview what was Received" @click="PreviewReceived(row)">
                                Received
                            </button>
                        </template>
                    </template>
                </template>
            </ChoiceTable>
        </div>
    </div>
    <div class="row mt-2" v-if="formData.tabName == CaseReportSummaryTabName.Unsolicited">
        <div class="col">
            <ChoiceTable 
                :columns="unsolicitedColumns" 
                :data="formData.unsolicitedList" 
                :show-filters="false" 
                :allowSorting="true"
                :height="tableHeight"
                fontSize="smaller"
                :key-value-data-member="'Col3Val'">
                <template #cell="{col, row, value}">
                    <template v-if="'Preview' === col.Name">
                        <button class="btn btn-primary btn-sm ms-1 pt-0 pb-0" title="Preview what was Received" @click="PreviewReceivedUnSolicited(row)">
                            Received
                        </button>
                    </template>
                </template>
            </ChoiceTable>
        </div>
    </div>

    <ModalDialog :show="formData.showExportQuestion" :size="ModalSize.Small" @hide="onHideExportQuestion">
        <template #title>Do you wish to include unsolicited records?</template>
        <div class="row mt-2">
            <div class="col d-flex justify-content-end">
                <ActionButtonV2 
                    :action="ActionButtonStyle.Check" 
                    text="Yes"
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                    :performAction="() => CaseReportExport(true, true)"
                    :size="ButtonDisplaySize.Small"
                    :endingPadding="1" />
                <ActionButtonV2 
                    :action="ActionButtonStyle.Close" 
                    text="No"
                    :displayStyle="ButtonDisplayStyle.OutlinePrimary"  
                    :performAction="() => CaseReportExport(true, false)"
                    :size="ButtonDisplaySize.Small"
                    :endingPadding="1" />            
            </div>
        </div>
    </ModalDialog>
</template>

